import { useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { ShiftDetailsInviteSection } from '~/constants/ff.posthog';
import {
  confirmSourceFromEmailNotification,
  setConfirmCandidateSource,
} from '~/domain/ConfirmCandidateSource';
import { parseNotificationParams } from '~/domain/OfficeNotification';
import { useFeatureFlagVariant } from '~/hooks/useFeatureFlagVariant';
import OpenJobScreen from './OpenJobScreen';
import OpenJobScreenRedesign from './OpenJobScreenRedesign';

/**
 * Experiment gate for the open Shift/Interview Details page. The
 * `ShiftDetailsInviteSection` (GOTU-5384) experiment buckets offices into
 * `test` (redesigned two-column screen) and `control` (legacy single-column
 * screen, untouched). Reading the variant here also drives PostHog exposure.
 *
 * Also the place where a "candidate applied" email landing is attributed for
 * GOTU-4906. This route is where those CTAs land, and for a pending/counter shift
 * both variants immediately `history.replace` to the confirm screen — which drops
 * the query string, and with it the `utm_campaign` telling us which email it was.
 * Recording the source here, above the variant split, captures it once for both
 * arms and before the redirect.
 */
export default function OpenJobScreenSwitch() {
  const isTestVariant = useFeatureFlagVariant(ShiftDetailsInviteSection, 'test');
  const params = useParams<{ jobId?: string }>();
  const [searchParams] = useSearchParams();

  // Read during render so the params are still present: the legacy arm's
  // `useOfficeNotificationLanding` strips them from the URL on mount, and child
  // effects run before this one.
  const { origin, notification } = parseNotificationParams(searchParams);
  // Gate on `origin` so only a real email landing (`utm_source=email`) is
  // attributed to email, not any link that happens to carry the campaign.
  const emailSource =
    origin === 'email' ? confirmSourceFromEmailNotification(notification) : undefined;

  useEffect(() => {
    if (!emailSource) {
      return;
    }

    setConfirmCandidateSource({ source: emailSource, shiftId: params.jobId });
    // Fires once per landing. A later re-render after the params are stripped must
    // not clear what this recorded, and the office can only land here once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return isTestVariant ? <OpenJobScreenRedesign /> : <OpenJobScreen />;
}
